import { useQuery } from '@tanstack/react-query';
import { BoundingBox, Dealer, PreferenceType } from '../dealer.types';
import { useDealerMapContext } from '../context/dealer-map-context';
import { useEffect } from 'react';
import { sortDealersByDescription, sortDealersByDistance } from '../utils/dealer-map-utils';
import { getDealerHttpService } from '../../../domain/http/http.factory';
import { BackendRoutes } from '../dealer.const';

export const useDealerLocations = () => {
  const {
    services,
    queryKeys,
    setSelectedDealer,
    selectedDealer,
    initialRequestType,
    initialDealerHasBeenSelected,
    setInitialDealerHasBeenSelected,
    initiallySelectedDealer,
    selectedFilterOptions,
    dealerSearchQueryObject,
  } = useDealerMapContext();

  const favoriteDealersQuery = useQuery(
    queryKeys?.get('FAVORITES') || ['DEALERS', 'FAVORITES'],
    async () => {
      const dealers = await getDealerHttpService().get<any[]>(BackendRoutes.DEALER_PREFERENCES);
      const dealerToReturn: Dealer[] = [];
      if (initialRequestType === 'MAIN_DEALER') {
        const myDealer = dealers.filter((dealer) => dealer.preference?.type === PreferenceType.MAIN);

        if (myDealer.length > 0) {
          dealerToReturn.push(...myDealer);
        } else {
          // Fallback if no "my dealer" is but there are favorites
          dealerToReturn.push(...(dealers || []));
        }
      } else {
        dealerToReturn.push(...dealers);
      }

      if (!initialDealerHasBeenSelected && !!initiallySelectedDealer) {
        let dealerToSelect: Dealer | undefined;
        if (initiallySelectedDealer === 'MAIN_DEALER') {
          dealerToSelect = dealers.find((dealer) => dealer.preference?.type === PreferenceType.MAIN);
        } else {
          dealerToSelect = dealers.find((dealer) => dealer.id === initiallySelectedDealer);
        }

        setSelectedDealer?.(dealerToSelect);
        setInitialDealerHasBeenSelected?.(true);
      }

      return sortDealersByDescription(dealerToReturn);
    },
    { refetchOnWindowFocus: false }
  );

  const dealersSearchQuery = useQuery(
    queryKeys?.get('LOCATION') || ['DEALERS', 'LOCATION'],
    async () => {
      const queryParams: string[] = [];

      switch (dealerSearchQueryObject?.type) {
        case 'BOUNDING_BOX':
          queryParams.push(`minLat=${(dealerSearchQueryObject.value as BoundingBox)?.minLat}`);
          queryParams.push(`maxLat=${(dealerSearchQueryObject.value as BoundingBox)?.maxLat}`);
          queryParams.push(`minLng=${(dealerSearchQueryObject.value as BoundingBox)?.minLng}`);
          queryParams.push(`maxLng=${(dealerSearchQueryObject.value as BoundingBox)?.maxLng}`);
          break;
        case 'LOCATION':
          if (typeof dealerSearchQueryObject.value === 'string') {
            queryParams.push(dealerSearchQueryObject.value);
          } else {
            queryParams.push(`lat=${(dealerSearchQueryObject.value as google.maps.LatLng)?.lat()}`);
            queryParams.push(`lng=${(dealerSearchQueryObject.value as google.maps.LatLng)?.lng()}`);
          }
          if ('radiusKm' in dealerSearchQueryObject && dealerSearchQueryObject.radiusKm) {
            queryParams.push(`radiusKm=${dealerSearchQueryObject.radiusKm}`);
          }
          break;
        case 'REFERENCE':
          queryParams.push(`reference=${dealerSearchQueryObject.value}`);
          break;
        default:
          break;
      }

      if (selectedFilterOptions?.includes('imow')) {
        queryParams.push('iMOWDealerOnly=true');
      }

      const url = `${BackendRoutes.DEALERS}?${queryParams.join('&')}`;
      const dealers = await getDealerHttpService().get<Dealer[]>(url);
      return sortDealersByDistance(dealers);
    },
    {
      enabled: !!dealerSearchQueryObject?.value,
      refetchOnWindowFocus: false,
    }
  );

  const loadQueryPredictions = (inputValue: string, callback: (options) => void) => {
    if (inputValue && services?.autocompleteService) {
      services?.autocompleteService.getQueryPredictions({ input: inputValue }, (predictions) => {
        const filteredPredictions =
          predictions
            ?.filter(
              (
                prediction: google.maps.places.QueryAutocompletePrediction & {
                  reference: string;
                }
              ) => prediction.reference
            )
            .map((prediction: google.maps.places.QueryAutocompletePrediction & { reference: string }) => ({
              type: 'REFERENCE',
              value: prediction.reference,
              label: prediction.description,
            })) || []; // Ensure it's an empty array if undefined

        const options = filteredPredictions.length > 0 ? filteredPredictions : [];
        callback(options);
      });
    } else {
      // Return empty state item when input is empty or service is not available
      callback([{ type: 'EMPTY', value: '', label: 'No results found' }]);
    }
  };

  useEffect(() => {
    if (selectedDealer && favoriteDealersQuery.data) {
      const dealer = favoriteDealersQuery.data.find((d) => d.id === selectedDealer.id);
      if (dealer) {
        setSelectedDealer?.(dealer);
      }
    }
  }, [favoriteDealersQuery.data]);

  useEffect(() => {
    if (selectedDealer && dealersSearchQuery.data) {
      const dealer = dealersSearchQuery.data.find((d) => d.id === selectedDealer.id);
      if (dealer) {
        setSelectedDealer?.(dealer);
      }
    }
  }, [dealersSearchQuery.data]);

  return {
    dealersSearchQuery,
    favoriteDealersQuery,
    loadQueryPredictions,
  };
};
